import i18n from '@/i18n';
import { expiresAtWithStableJitter } from '@/utils/cacheExpiryJitter';

/** 节点 16.2：用户端 /api/system/models 短期内存缓存 TTL */
export const SYSTEM_MODEL_USER_LIST_CACHE_TTL_MS = 5 * 60 * 1000;
const SYSTEM_MODEL_USER_LIST_CACHE_JITTER_SPREAD_MS = 60 * 1000;

const cache = new Map();
/** 节点 16.5：保留最近一次成功响应，API 故障时可降级读取 */
const staleCache = new Map();
const inflight = new Map();

/**
 * 缓存键：featureCode + 筛选参数（modelType / garmentType / ethnicity）+ 分页。
 */
export function buildSystemModelUserListCacheKey(params = {}) {
  const featureCode = String(params.featureCode || '').trim();
  const modelType = String(params.modelType || '').trim();
  const garmentType = String(params.garmentType || '').trim();
  const ethnicity = String(params.ethnicity || '').trim();
  const page = Number(params.page) > 0 ? Number(params.page) : 1;
  const pageSize = Number(params.pageSize) > 0 ? Number(params.pageSize) : 200;
  return [featureCode, modelType, garmentType, ethnicity, page, pageSize].join('|');
}

export function readSystemModelUserListCache(key, now = Date.now()) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= now) {
    cache.delete(key);
    return null;
  }
  return entry.value;
}

export function writeSystemModelUserListCache(
  key,
  value,
  ttlMs = SYSTEM_MODEL_USER_LIST_CACHE_TTL_MS,
  now = Date.now(),
) {
  const entry = {
    expiresAt: expiresAtWithStableJitter(
      now,
      ttlMs,
      key,
      SYSTEM_MODEL_USER_LIST_CACHE_JITTER_SPREAD_MS,
    ),
    value,
  };
  cache.set(key, entry);
  staleCache.set(key, entry);
}

/** 读取过期缓存（节点 16.5：API 故障时优先于静态 fallback） */
export function readSystemModelUserListStaleCache(key) {
  return staleCache.get(key)?.value ?? null;
}

export function clearSystemModelUserListCache() {
  cache.clear();
  staleCache.clear();
  inflight.clear();
}

/**
 * 带 TTL 的 list 缓存；同 key 并发请求合并为单次 fetch。
 * @param {Record<string, unknown>} params
 * @param {(query: Record<string, unknown>) => Promise<unknown>} fetcher
 */
export async function listSystemModelsWithCache(params = {}, fetcher) {
  if (typeof fetcher !== 'function') {
    throw new Error(i18n.t('workbench:ipeUtilError.missingSystemModelListFn'));
  }

  const key = buildSystemModelUserListCacheKey(params);
  const cached = readSystemModelUserListCache(key);
  if (cached) return cached;

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const promise = Promise.resolve(fetcher(params))
    .then((result) => {
      writeSystemModelUserListCache(key, result);
      return result;
    })
    .catch((error) => {
      const stale = readSystemModelUserListStaleCache(key);
      const items = Array.isArray(stale?.items) ? stale.items : [];
      if (items.length > 0) {
        return { ...stale, staleCacheUsed: true };
      }
      throw error;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/** @internal 测试用：读取 entry 的 expiresAt */
export function readSystemModelUserListCacheExpiresAt(key) {
  return cache.get(key)?.expiresAt ?? null;
}
